import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  SectionList,
  StyleSheet,
  View,
  Text,
  ViewStyle,
  ImageSourcePropType,
  StyleProp,
  ImageStyle,
} from "react-native";
import {
  contactListSearchMatches,
  getAvatarByUser,
  getDisplayName,
  userListByLetter,
} from "../../utils/utils";
import { getUid } from "../../store/token";
import { User } from "../../../imsdk/db/t_user";
import * as ImSdk from "../../../imsdk/api";
import {
  color_333333,
  color_666666,
  color_BBBBBB,
  color_E9E9E9,
  color_EDEEF1,
  color_EEF3FA,
  color_main,
  white,
} from "../../assets/style/colors";
import { CListItem } from "./CListItem";
import { ListEmpty } from "../../components/ListEmpty";
import { CSearchInput } from "../../components/CSearchInput";
import NavBar from "../../components/NavBar";
import BaseScreen from "../BaseScreen";
import { Str_Confirm, Str_Search } from "../../language/string_res";
import { Letter } from "../../components/Letter";
import getItemLayout from "../GetItemLayout";
import { ButtonView } from "../../components/ButtonView";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { isIos } from "../../AppEvent";
import { GroupMember } from "../../../imsdk/db/t_group_member";
import { ScreenCloseModal } from "../../components/ScreenModal";

interface TopicUserViewProps {
  onClick: (item: User) => void;
  title?: string;
  onClose?: () => void;
  tid: string;
  filterSelf?: boolean;
  users?: (User | GroupMember)[];
}

type SectionProps = {
  title: string;
  data: User[];
};

export async function requestTopicUsers(
  tid: string
): Promise<GroupMember[] | undefined> {
  const r1 = await ImSdk.getInstance().GetGroupDetail(tid, true);
  return r1.members;
}

type ItemProps = {
  item: User;
  onClick: (item: User) => void;
  hasRadio?: boolean;
  isChecked?: boolean;
};

const RenderItem = (props: ItemProps) => {
  const name = getDisplayName(props.item);
  const left = getAvatarByUser(props.item);
  return (
    <CListItem
      id={props.item.uid}
      left={left}
      center={
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.title}>
          {name}
        </Text>
      }
      onClick={() => props.onClick(props.item)}
      hasRadio={props.hasRadio}
      isChecked={props.isChecked}
    />
  );
};
export const TopicUserListRender = (props: {
  users: (User | GroupMember)[];
  onClick?: (item: User) => void;
  onClose?: () => void;
  title?: string;
  backIcon?: ImageSourcePropType;
  backIconStyle?: StyleProp<ImageStyle>;
  hasRadio?: boolean;
  showHead?: boolean;
  statusHeight?: number;
  style?: ViewStyle;
  onSubmit?: (users: number[]) => void;
}) => {
  return (<ScreenCloseModal onClose={props.onClose}>
    <TopicUserListContentRender {...props} />
  </ScreenCloseModal>)
}

export const TopicUserListContentRender = (props: {
  users: (User | GroupMember)[];
  onClick?: (item: User) => void;
  onClose?: () => void;
  title?: string;
  backIcon?: ImageSourcePropType;
  backIconStyle?: StyleProp<ImageStyle>;
  hasRadio?: boolean;
  showHead?: boolean;
  statusHeight?: number;
  style?: ViewStyle;
  onSubmit?: (users: number[]) => void;
}) => {
  const [section, setSection] = useState<SectionProps[]>([]);
  const [searchVal, setSearchVal] = useState("");
  const [selecteds, updateSelecteds] = useState<number[]>([]);
  const sectionRef = useRef<SectionList<any, SectionProps>>(null);
  const insets = useSafeAreaInsets();
  const isBusy = useRef(false);

  const showHead = props.showHead ?? true;
  useEffect(() => {
    if (searchVal === "") {
      const ss = userListByLetter(props.users);
      setSection(ss);
    } else {
      const filterUsers = props.users.filter((v) => {
        if (!v) {
          return false;
        }
        return contactListSearchMatches(v, searchVal);
      });
      setSection(userListByLetter(filterUsers));
    }
  }, [props.users, searchVal]);

  function handleOnclick(item: User) {
    if (props.hasRadio) {
      const index = selecteds.indexOf(item.uid);
      if (index > -1) {
        selecteds.splice(index, 1);
      } else {
        selecteds.push(item.uid);
      }
      updateSelecteds([...selecteds]);
    }

    props.onClick?.(item);
  }
  const handleTextChange = useCallback((text: string) => {
    setSearchVal(text);
  }, []);

  return (
    <>
      <CSearchInput
        searchVal={searchVal}
        onChange={handleTextChange}
        placeholder={Str_Search}
      />
      <View style={{ flex: 1 }}>
        <SectionList
          ref={sectionRef}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          sections={section}
          keyboardDismissMode="on-drag"
          keyExtractor={(item, index) => item.uid.toString()}
          renderItem={({ item }: { item: User }) => (
            <RenderItem
              item={item}
              onClick={handleOnclick}
              isChecked={selecteds.includes(item.uid)}
              hasRadio={props.hasRadio}
            />
          )}
          renderSectionHeader={({ section: { title } }) => (
            <View
              style={{
                height: 40,
                justifyContent: "center",
                backgroundColor: color_EEF3FA,
              }}
            >
              <Text style={styles.sectionTitle}>{title}</Text>
            </View>
          )}
          ListEmptyComponent={<ListEmpty />}
          getItemLayout={getItemLayout({
            getItemHeight: 64,
            getSectionHeaderHeight: 40,
          })}
        />
        <Letter
          section={section}
          scrollToLocation={(index) => {
            sectionRef.current?.scrollToLocation({
              animated: true,
              itemIndex: 0,
              sectionIndex: index,
            });
          }}
        />
      </View>
      {props.hasRadio ? (
        <View
          style={{
            backgroundColor: color_EDEEF1,
            width: "100%",
            paddingBottom: isIos ? insets.bottom : 0,
            flexDirection: "row-reverse",
          }}
        >
          <ButtonView
            style={[
              styles.submitText,
              {
                backgroundColor: selecteds.length ? color_main : color_E9E9E9,
              },
            ]}
            textStyle={{ color: selecteds.length ? white : color_BBBBBB }}
            onPress={() => {
              if (selecteds.length) {
                if (isBusy.current) {
                  return;
                }
                isBusy.current = true;
                props.onSubmit?.(selecteds);
                isBusy.current = false;
              }
            }}
            text={
              selecteds.length
                ? `${Str_Confirm}(${selecteds.length})`
                : Str_Confirm
            }
          />
        </View>
      ) : null}
    </>
  );
};

export const TopicUserView = (props: TopicUserViewProps) => {
  const [users, setUsers] = useState<(User | GroupMember)[]>(props.users || []);
  useEffect(() => {
    if (!props.users || props.users.length === 0) {
      requestTopicUsers(props.tid).then((res) => {
        const uid = getUid();
        const userList = props.filterSelf
          ? res?.filter((item) => item.uid !== uid)
          : res;
        setUsers(userList ?? []);
      });
    }
  }, []);

  return (
    <TopicUserListContentRender
      style={{}}
      users={users}
      title={props.title}
      onClose={props.onClose}
      onClick={props.onClick}
    />
  );
};

const styles = StyleSheet.create({
  box: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    color: color_333333,
  },
  sectionTitle: {
    color: color_666666,
    paddingLeft: 12,
  },
  header: {
    display: "flex",
    backgroundColor: white,
    height: 42,
  },
  headerIcon: {
    position: "absolute",

    left: 16,
    top: 8,
    zIndex: 2,
  },
  headerTitle: {
    width: "100%",
    textAlign: "center",
    color: color_333333,
    fontSize: 16,
    lineHeight: 42,
    fontFamily: "",
    fontWeight: "bold",
  },
  submitText: {
    fontSize: 14,
    paddingVertical: 10,
    paddingHorizontal: 12,
    margin: 12,
    borderRadius: 4,
  },
});
